import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { Gift, Coins, Flame } from 'lucide-react';
import './WelcomeOverlay.css';

const WelcomeOverlay = () => {
  const { user } = useAuth();
  const [activeEvent, setActiveEvent] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user) return;

    // Solo mostrar si es el primer login detectado en esta sesión local
    // O si el usuario tiene un evento pendiente en el buzón que sea de tipo 'welcome_event'
    const checkWelcomeEvent = async () => {
      try {
        const hasShown = localStorage.getItem(`welcome_shown_${user.id}`);
        if (hasShown) return;

        const response = await axios.get('/api/messages');
        const messages = response.data.messages || [];
        
        // Buscar un mensaje de bienvenida que no haya sido reclamado
        const welcomeMessage = messages.find(m => 
          (m.metadata?.type === 'welcome_event' || m.metadata?.type === 'welcome_event_notification') && 
          !m.is_read
        );

        if (welcomeMessage) {
          setActiveEvent({
            id: welcomeMessage.metadata.event_id,
            name: welcomeMessage.title || '¡Bienvenido a MundoXYZ!',
            message: welcomeMessage.content || 'Disfruta de este regalo especial para comenzar tu aventura.',
            coins: welcomeMessage.metadata.coins_amount || 0,
            fires: welcomeMessage.metadata.fires_amount || 0,
            messageId: welcomeMessage.id
          });
          setIsVisible(true);
        }
      } catch (err) {
        console.error('Error checking welcome event:', err);
      }
    };

    checkWelcomeEvent();
  }, [user]);

  const handleClaim = async () => {
    if (!activeEvent || isClaiming) return;
    
    setIsClaiming(true);
    try {
      const response = await axios.post('/api/welcome/accept', { 
        event_id: activeEvent.id 
      });
      
      toast.success(`🎉 ¡Felicidades! Has recibido ${response.data.coins_received}🪙 y ${response.data.fires_received}🔥`);
      
      // Marcar mensaje como leído si existe
      if (activeEvent.messageId) {
        await axios.put(`/api/messages/${activeEvent.messageId}/read`);
      }

      localStorage.setItem(`welcome_shown_${user.id}`, 'true');
      setIsVisible(false);
      
      // Refrescar datos
      queryClient.invalidateQueries(['wallet-balance']);
      queryClient.invalidateQueries(['user-wallet']);
      queryClient.invalidateQueries(['header-balance']);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Error al reclamar el regalo';
      toast.error(errorMsg);
      if (errorMsg.includes('ya reclamaste')) {
        localStorage.setItem(`welcome_shown_${user.id}`, 'true');
        setIsVisible(false);
      }
    } finally {
      setIsClaiming(false);
    }
  };

  return (
    <AnimatePresence>
      {isVisible && activeEvent && (
        <motion.div 
          className="welcome-overlay-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div 
            className="welcome-overlay-card"
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <div className="welcome-overlay-icon">
              <Gift size={40} />
            </div>

            <h2 className="welcome-overlay-title">{activeEvent.name}</h2>
            <p className="welcome-overlay-message">{activeEvent.message}</p>

            <div className="welcome-overlay-rewards">
              <div className="welcome-reward-item">
                <div className="welcome-reward-value text-accent">
                  <Coins size={24} /> {activeEvent.coins}
                </div>
                <div className="welcome-reward-label">Coins</div>
              </div>
              <div className="welcome-reward-item">
                <div className="welcome-reward-value text-fire-orange">
                  <Flame size={24} /> {activeEvent.fires}
                </div>
                <div className="welcome-reward-label">Fires</div>
              </div>
            </div>

            <button 
              className="welcome-accept-btn"
              onClick={handleClaim}
              disabled={isClaiming}
            >
              {isClaiming ? 'Procesando...' : '🎁 Aceptar Regalo'}
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default WelcomeOverlay;
